import { z } from 'zod';

export const uuidSchema = z.string().uuid();

export const paginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const timestampSchema = z.string().datetime();

export const idParamSchema = z.object({
  id: uuidSchema,
});

/**
 * Serialized length of `value` (`JSON.stringify(...).length`), or `null` when it
 * cannot be serialized.
 *
 * Measuring a caller-supplied value with a bare `JSON.stringify(v).length`
 * throws: `JSON.stringify` recurses, and `JSON.parse` accepts nesting depths it
 * cannot re-serialize (~10k deep), so a body the request parser ALREADY accepted
 * raises `RangeError: Maximum call stack size exceeded` inside the refinement —
 * escaping `safeParse` and turning malformed input into a 500.
 *
 * Callers reject on `null`, failing closed: a payload we cannot measure is one we
 * cannot admit under a size cap.
 */
export function jsonLengthOrNull(value: unknown): number | null {
  try {
    const json = JSON.stringify(value);
    return typeof json === 'string' ? json.length : null;
  } catch {
    return null;
  }
}

/** Size-cap refinement over arbitrary caller JSON. Rejects the unmeasurable. */
export function withinJsonLength(value: unknown, max: number): boolean {
  const length = jsonLengthOrNull(value);
  return length !== null && length <= max;
}

// =============================================================================
// WIRE HELPERS
// =============================================================================

/**
 * Optional on the wire, where a client may send `null` to mean "absent".
 *
 * `.optional()` alone REJECTS an explicit null, and null is reachable from real
 * clients. Every consumer collapses null and absent to the same thing, so normalize
 * to undefined rather than 400 a request that worked before the contract existed.
 */
export const clientOptionalString = z.string().nullish().transform((v) => v ?? undefined);
export const clientOptionalBoolean = z.boolean().nullish().transform((v) => v ?? undefined);
export const clientOptionalNumber = z.number().nullish().transform((v) => v ?? undefined);

// =============================================================================
// MESSAGE ATTACHMENTS
// =============================================================================

/**
 * One inbound base64 attachment on a message.
 *
 * Shared, not duplicated, by the two transports that accept a file on an inbound
 * message: the `app` channel (`appMessageBody`) and the send-message test endpoint
 * (`sendMessageBody`) — which inserts over that same `app` channel. They MUST NOT
 * drift: `wayai send-message --file` exists so an image flow can be exercised in the
 * dev loop exactly as a real channel delivers it. A shape one side accepts and the
 * other rejects makes the dev loop lie about the behaviour it exists to reproduce.
 */
export const messageAttachment = z.object({
  /**
   * Capped at the same 255 the storage layer already truncates names to, so nothing
   * legitimate is lost — every filesystem caps there, and `sanitizeFileName` sliced
   * longer names anyway.
   *
   * The bound is load-bearing, not hygiene: the RAW name is written to R2
   * `customMetadata.originalFileName`, and R2 rejects oversized metadata. An unbounded
   * name let a client make the R2 write throw a generic storage error — indistinguishable
   * from a real outage — which pages us via `logger.error` and, on the app channel, is
   * read as "we lost the file" and escalates the user to a human. Client input must not
   * be able to impersonate an infrastructure failure (gh #3365).
   */
  file_name: z.string().max(255),
  /** Base64. Bounded by the route's bodyLimit, not by this schema. */
  file_binary: z.string(),
  file_size: clientOptionalNumber,
  file_extension: clientOptionalString,
});
export type MessageAttachment = z.infer<typeof messageAttachment>;

/**
 * Cap on an inbound message body, in bytes.
 *
 * ONE constant for both transports that accept an attachment — the `app` channel
 * (`/channels/*`) and send-message — because a file the real channel accepts must not
 * 413 on the endpoint that exists to rehearse it. Written twice, the two drift and the
 * dev loop quietly stops matching production. The CLI derives its pre-flight budget
 * from this same number, so it can reject oversize files with a message naming them
 * instead of surfacing a 413 whose body no one sees.
 */
export const MAX_MESSAGE_BODY_BYTES = 10 * 1024 * 1024;

/**
 * Upper bound on attachments per message. Both routes upload them SEQUENTIALLY (one R2
 * write + one DO storeFile round-trip each), so an unbounded array let any authenticated
 * user turn a single request into ~10^5 sequential R2 writes — a request-timeout and
 * a per-message write budget bounded only by body bytes (CLAUDE.md Cost Invariants). No
 * real chat message carries anywhere near this many; no client has a bulk-attach flow.
 */
export const MAX_ATTACHMENTS_PER_MESSAGE = 20;

/**
 * Cap on a single audio payload headed for STT, as base64 length (≈4/3 of decoded
 * bytes; 8 MB ≈ 6 MB of audio — plenty for voice notes). ONE constant for every
 * surface that accepts audio for transcription — the `app` channel's `audio_file`,
 * send-message audio attachments, and the CLI's pre-flight — so a file one accepts
 * cannot be rejected by another, and the CLI can name the offending file instead of
 * surfacing a server 400.
 */
export const MAX_AUDIO_FILE_BASE64_BYTES = 8 * 1024 * 1024;

/**
 * True when a send-message attachment should be routed to STT (transcribed) rather
 * than delivered to the LLM as a file. Extension-based on purpose: the CLI has no
 * mime derivation (that lives in `@wayai/runtime`), and the backend's mime map is
 * itself keyed by extension, so classifying on the extension keeps CLI pre-flight
 * and route behavior identical by construction. Deliberately STRICT-audio — no
 * `.mp4`: video files keep their existing treatment as plain attachments, and the
 * DO-side channel predicate (`audio/*` mime + Instagram's video/mp4 quirk) is a
 * superset, so anything classified here always takes the STT path in the DO.
 */
export function isAudioAttachmentFileName(fileName: string): boolean {
  return /\.(mp3|ogg|oga|wav|m4a|webm)$/i.test(fileName);
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

/** Generic JSON-compatible value (replaces the old Supabase-generated Json type) */
export type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[];

// =============================================================================
// RESPONSE TYPE WRAPPERS
// =============================================================================

export interface ApiErrorResponse {
  error: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ApiDataResponse<T> {
  data: T;
}

export interface ApiListResponse<T> {
  data: T[];
}

export interface ApiSuccessResponse {
  success: true;
}
